/**
 * Recap バッジ / ラベルの enum → 種別 mapping 純関数群。
 *
 * 規約変更 (D タスク: i18n ハードコード禁止):
 *   以前は label (日本語文字列) を返していたが、 それは i18n ハードコード禁止規約
 *   に違反する。 本モジュールは種別 (kind) のみを返し、 label は呼び出し元の
 *   component 側で t() 経由で解決する。
 *
 * spec ↔ impl drift 注記 (自走判断):
 *   当初のテスト設計は design/ プロトタイプの CSS class (badge-buy 等) を前提に
 *   「クラス/ラベル解決関数」 を期待しているが、 実装は inline JSX + ecloud/chakra
 *   color props で組まれており、 CSS class 名は持たない。 そこで本モジュールは
 *   「種別 (kind) を返す純粋 resolver」 を提供する (= status の意味的分類を関数で
 *   解決する。 買/売・約定経路・受渡区分・オプション有無・通告タイミングの解決は
 *   他モジュールへ移管、 または production 参照が 0 になり削除済み — 詳細は下記)。
 *   色そのものは F3 VRT / F2 component の責務に委ねる。
 */
import { TradingContractStatus } from '@ebalance/proto/bfftypes/v1/trading_contract_status_pb'

// 受渡区分 (TC-C-05) の種別解決は canonical な ~/models/deliveryType に集約済み。
// 本モジュールに pass-through wrapper を置かない (= 二重定義を避ける)。

// 売買区分の種別解決 (旧 resolveBuySellKind / BuySellKind) は、置換により
// production 参照が 0 になったため削除済み (design:
// trade-side-color-semantic.md AC-14。売買方向の描画は
// ~/components/common/TradeSideLabel が proto enum を直接受けて解決する)。

// 約定経路 (TC-C-03 / TC-C-04) の種別解決 (旧 ExecutionRouteKind /
// resolveExecutionRouteKind) は、置換により production 参照が 0 になったため
// 削除済み (バッジの色分けは Badges/index.tsx の `EXECUTION_ROUTE_BADGE_STATUS`
// が proto enum を直接受けて解決する)。

// ---------------------------------------------------------------------------
// ステータス (TC-C-06)
// ---------------------------------------------------------------------------

export type StatusKind = 'draft' | 'active' | 'expired' | 'cancelled'

/**
 * 契約ステータスから種別を解決する。
 * TC-C-06.1: 全 4 値が互いに区別できる。
 * label は呼び出し元で t('page:supplyBilateral.status.<kind>') 経由で解決する。
 */
export function resolveStatusKind(status: TradingContractStatus): StatusKind {
  switch (status) {
    case TradingContractStatus.DRAFT:
      return 'draft'
    case TradingContractStatus.ACTIVE:
      return 'active'
    case TradingContractStatus.EXPIRED:
      return 'expired'
    case TradingContractStatus.CANCELLED:
      return 'cancelled'
    default:
      return 'draft'
  }
}

// オプション有無 (TC-C-07、旧 OptionKind / resolveOptionKind) と通告タイミング
// (AC-009、旧 NotificationTimingKind / resolveNotificationTimingKind) の種別
// 解決は、 相対取引一覧・Recap 画面からのオプション表記撤去 (spec
// bilateral-trade-recap.md AC-SBR-008〜010 欠番、 通告変更管理 P3 へ移管) により
// production 参照が 0 になったため削除済み。
